import { useState, useCallback } from 'react'
import {
  GraduationCap, Briefcase, ChevronDown, Filter,
  Users, CheckCircle, AlertCircle, Info
} from 'lucide-react'
import AnchorLink from './AnchorLink'
import { Table } from '@anyemp/ui-kit'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-6" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const Tag = ({ children, color = 'gray' }) => {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    amber:  'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    gray:   'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary border-light-border-primary dark:border-dark-border-primary',
  }
  return (
    <span className={`wiki-mono inline-flex items-center text-[11px] px-1.5 py-0.5 rounded border ${colors[color]}`}>
      {children}
    </span>
  )
}

const FilterBadge = ({ label, value }) => (
  <div className="inline-flex items-center gap-1 text-xs">
    <span className="text-light-text-tertiary dark:text-dark-text-tertiary">{label}:</span>
    <span className="wiki-mono text-[11px] px-1.5 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-light-text-primary dark:text-dark-text-primary">
      {value}
    </span>
  </div>
)

const SectionCard = ({ route, label, icon: Icon, iconColor, iconBg, filters, logic, note, children }) => (
  <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
    {/* Header */}
    <div className="flex items-center gap-3 px-4 py-3 bg-light-bg-secondary dark:bg-dark-bg-secondary border-b border-light-border-primary dark:border-dark-border-primary">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">{label}</span>
        <span className="wiki-mono text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary ml-2">{route}</span>
      </div>
    </div>
    {/* Body */}
    <div className="px-4 py-3 space-y-3">
      {/* Filters row */}
      <div className="flex flex-wrap gap-2">
        {filters.map((f, i) => <FilterBadge key={i} label={f.label} value={f.value} />)}
      </div>
      {/* Logic explanation */}
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">
        {logic}
      </p>
      {/* Status list */}
      {children}
      {/* Note */}
      {note && (
        <div className="flex gap-2 items-start pt-1">
          <Info className="w-3.5 h-3.5 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0 mt-0.5" />
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{note}</p>
        </div>
      )}
    </div>
  </div>
)

const StatusList = ({ statuses, color = 'gray' }) => (
  <div className="flex flex-wrap gap-1.5">
    {statuses.map(s => <Tag key={s} color={color}>{s}</Tag>)}
  </div>
)

const faqItems = [
  {
    key: 'nav-q1',
    question: 'A candidate is in "Candidates" but doesn\'t appear in "Interview" — why?',
    answer: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        The "Interview" section shows candidates whose <strong>current stage</strong> (current_stage_id) is Interview — status doesn't matter, so this isn't a status-name mismatch. Check the candidate's <strong>Stage</strong> chip on their profile (separate from their Status chip) — if it's not Interview, that's why. A stage change usually needs to be done explicitly; it doesn't always follow a status change.
      </p>
    )
  },
  {
    key: 'nav-q2',
    question: 'Why can the same person appear in multiple sections?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1">
        <p>The main <strong>Candidates</strong> section shows everyone who has ever been a candidate (including current ones). Pipeline subsections (Video, Interview, etc.) filter by <em>current stage</em>. So a candidate on the "Interview" stage is also visible in the general "Candidates" list.</p>
      </div>
    )
  },
  {
    key: 'nav-q3',
    question: 'What\'s the difference between "Employees" and "All Employees"?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p><strong>Employees</strong> — only those who are <em>currently</em> employees and marked as active (<Tag>is_active = true</Tag> + <Tag>current_only = true</Tag>). Dismissed, inactive, and former employees (converted to another type) are not shown.</p>
        <p><strong>All Employees</strong> — all records with type employee without any restrictions. Used for audits and administrative tasks.</p>
      </div>
    )
  },
  {
    key: 'nav-q4',
    question: 'What does "current_only" mean?',
    answer: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        The <Tag>current_only = true</Tag> flag means: show only those whose <strong>talent_type</strong> field in the database is currently equal to the required type. For example, if a person was a candidate and was then hired (type changed to employee) — with current_only they will no longer appear in candidates.
      </p>
    )
  },
  {
    key: 'nav-q5',
    question: 'Are the status/stage names in subsections fixed values?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>Candidate pipeline subsections (<strong>Video, Interview, Onboarding, ...</strong>) filter by <em>stage</em>, not status name — the list of stages comes from Stage Management (GET /stages?talent_type=candidate), so a new stage there gets its own page automatically. <strong>Rejected</strong> is still status-name based (<Tag>Not Approved</Tag>, <Tag>Denied</Tag>, <Tag>Refused</Tag>).</p>
        <p>Employee subsections (<strong>Active Employees, Project Employees</strong>) take status names from the <Tag>shared/talentStatuses.json</Tag> file — the list can be changed centrally.</p>
      </div>
    )
  },
]

const SUMMARY_COLUMNS = [
  {
    key: 'name',
    label: 'Section',
    render: (row) => (
      <span className="font-medium text-light-text-primary dark:text-dark-text-primary wiki-heading">{row.name}</span>
    ),
  },
  {
    key: 'co',
    label: 'current_only',
    render: (row) => row.co === '✓'
      ? <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
      : <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>,
  },
  {
    key: 'ia',
    label: 'is_active',
    render: (row) => row.ia === '✓'
      ? <span className="text-green-600 dark:text-green-400 font-bold">✓</span>
      : <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>,
  },
  {
    key: 'status',
    label: 'Status filter',
    render: (row) => row.status === '—'
      ? <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>
      : <Tag>{row.status}</Tag>,
  },
  {
    key: 'desc',
    label: "What's shown",
    render: (row) => (
      <span className="text-light-text-secondary dark:text-dark-text-secondary">{row.desc}</span>
    ),
  },
]

const SUMMARY_DATA = [
  { name: 'Candidates',       co: '—', ia: '—', status: '—',                       desc: 'Everyone who has ever been a candidate' },
  { name: 'Video / Interview / Onboarding / ...', co: '—', ia: '—', status: '— (stage-based)', desc: 'Current stage = this pipeline stage, any status — one dynamic page per stage from GET /stages?talent_type=candidate' },
  { name: 'Rejected',         co: '—', ia: '—', status: 'Not Approved / Denied / Refused', desc: 'Current status is one of three (status-based, not a pipeline stage)' },
  { name: 'Employees',        co: '✓', ia: '✓', status: '—',                       desc: 'Active employees (any status)' },
  { name: 'Active Employees', co: '✓', ia: '✓', status: '5 statuses',              desc: 'Active employees in working statuses' },
  { name: 'Project Employees',co: '✓', ia: '✓', status: '3 statuses',              desc: 'Active employees on projects' },
  { name: 'All Employees',    co: '—', ia: '—', status: '—',                       desc: 'All employees with no filters (including dismissed)' },
]

const NavigationSectionsGuide = () => {
  const [expandedBlocks, setExpandedBlocks] = useState({})
  const navRowKey = useCallback((row) => row.name, [])
  const toggleBlock = useCallback(
    (key) => setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] })),
    []
  )

  return (
    <div className="space-y-10">

      {/* Intro */}
      <div>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">
          Each talent type in the left sidebar has subsections — they are not just labels, but real filters with different logic.
          The same record can appear in multiple sections or none at all — depending on which filters are applied.
          Below is the exact description of each section.
        </p>
      </div>

      {/* Candidates */}
      <div>
        <SubSectionHeading id="nav-candidates">Candidates — subsections</SubSectionHeading>

        <div className="mb-4 pl-4 border-l-2 border-blue-400/50 bg-blue-50/40 dark:bg-blue-900/10 rounded-r-lg py-3 pr-4">
          <p className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-1 flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-blue-500" /> General principle for candidates
          </p>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            None of the candidate sections have a <Tag>current_only</Tag> or <Tag>is_active</Tag> filter.
            This means: if a person was converted from candidate to employee — they <em>still</em> appear in the general candidates list.
            Pipeline subsections (Video, Interview, Onboarding...) filter by <strong>current stage</strong> (current_stage_id) — a separate axis from status. <strong>Rejected</strong> is the one exception and still filters by status name (Not Approved / Denied / Refused), since "rejected" isn't a pipeline stage.
          </p>
        </div>

        <div className="space-y-4">

          <SectionCard
            route="/candidates"
            label="Candidates"
            icon={GraduationCap}
            iconColor="text-blue-600 dark:text-blue-400"
            iconBg="bg-blue-50 dark:bg-blue-900/20"
            filters={[
              { label: 'talent_type', value: 'candidate' },
              { label: 'current_only', value: 'false (historical)' },
            ]}
            logic="Shows everyone whose talent_type was ever 'candidate'. Includes people who are now employees or presale — if they previously went through the candidate path. No status filter applied."
            note="This is the broadest list — a historical selection of all candidates."
          />

          <SectionCard
            route="/candidates/:stageKey"
            label="Pipeline stages (Video, Interview, Onboarding...)"
            icon={Filter}
            iconColor="text-blue-600 dark:text-blue-400"
            iconBg="bg-blue-50 dark:bg-blue-900/20"
            filters={[
              { label: 'talent_type', value: 'candidate' },
              { label: 'stage_keys', value: '<stage key from the URL>' },
            ]}
            logic='One page for every candidate pipeline stage — the list of stages (and the matching sidebar entries) is loaded from GET /stages?talent_type=candidate, not hard-coded. A candidate stays visible on their stage regardless of status: someone on "Video" with status Approved, New, or Denied still shows up here, not just status = "Video".'
            note='Adding a new stage to the pipeline (Stage Management page) makes its /candidates/:stageKey page and sidebar entry appear automatically — no code change needed.'
          >
            <StatusList statuses={['Video', 'Interview', 'Pre-onboarding', 'Onboarding', '...']} color="blue" />
          </SectionCard>

        </div>
      </div>

      {/* Employees */}
      <div>
        <SubSectionHeading id="nav-employees">Employees — subsections</SubSectionHeading>

        <div className="mb-4 pl-4 border-l-2 border-green-400/50 bg-green-50/40 dark:bg-green-900/10 rounded-r-lg py-3 pr-4">
          <p className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary mb-1 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-green-500" /> General principle for employees
          </p>
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            Unlike candidates, most employee sections use strict filters:
            <Tag className="mx-1">current_only = true</Tag> — only those whose <em>current</em> type is employee,
            and <Tag className="mx-1">is_active = true</Tag> — only active (not dismissed).
            Exception — <strong>All Employees</strong>, which has no restrictions.
          </p>
        </div>

        <div className="space-y-4">

          <SectionCard
            route="/employees"
            label="Employees"
            icon={Briefcase}
            iconColor="text-green-600 dark:text-green-400"
            iconBg="bg-green-50 dark:bg-green-900/20"
            filters={[
              { label: 'talent_type', value: 'employee' },
              { label: 'current_only', value: 'true' },
              { label: 'is_active', value: 'true' },
            ]}
            logic="Shows only those who are currently an employee (current_only) and marked as active (is_active). Former employees converted to another type and inactive employees are not shown. No status filter."
            note="This is the working employee list — the most up-to-date view."
          />

          <SectionCard
            route="/employees-active"
            label="Active Employees"
            icon={CheckCircle}
            iconColor="text-green-600 dark:text-green-400"
            iconBg="bg-green-50 dark:bg-green-900/20"
            filters={[
              { label: 'talent_type', value: 'employee' },
              { label: 'current_only', value: 'true' },
              { label: 'is_active', value: 'true' },
              { label: 'status_names', value: '5 statuses (ACTIVE_STATUSES)' },
            ]}
            logic="Employees with current_only + is_active, whose current status is in the ACTIVE_STATUSES list from config. These are employees actually working or available."
            note="The ACTIVE_STATUSES list is stored in shared/talentStatuses.json and can be changed by an administrator."
          >
            <StatusList statuses={['Available', 'Work', 'Full Project + Part Time', 'Part Project + Full Time', 'Part Project + Part Time']} color="green" />
          </SectionCard>

          <SectionCard
            route="/employees-project"
            label="Project Employees"
            icon={Users}
            iconColor="text-purple-600 dark:text-purple-400"
            iconBg="bg-purple-50 dark:bg-purple-900/20"
            filters={[
              { label: 'talent_type', value: 'employee' },
              { label: 'current_only', value: 'true' },
              { label: 'is_active', value: 'true' },
              { label: 'status_names', value: '3 statuses (PROJECT_STATUSES)' },
            ]}
            logic="Employees with current_only + is_active, whose status is in PROJECT_STATUSES. These are employees assigned to specific projects."
            note="The PROJECT_STATUSES list is stored in shared/talentStatuses.json."
          >
            <StatusList statuses={['Project', 'Part Project + Part Project', 'Part Project  + Presale']} color="purple" />
          </SectionCard>

          <SectionCard
            route="/employees-all"
            label="All Employees"
            icon={AlertCircle}
            iconColor="text-amber-600 dark:text-amber-400"
            iconBg="bg-amber-50 dark:bg-amber-900/20"
            filters={[
              { label: 'endpoint', value: '/talents/type/employee/all' },
              { label: 'current_only', value: 'not applied' },
              { label: 'is_active', value: 'not applied' },
            ]}
            logic="Uses a separate API endpoint with no type or activity filters. Shows absolutely everyone whose talent_type = 'employee' — including dismissed, inactive, and those later converted to another type."
            note="This section is for administrative tasks and audits — not for daily work."
          />

        </div>
      </div>

      {/* How status matching works */}
      <div>
        <SubSectionHeading id="nav-how-status-match">How status name filtering works</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4 leading-relaxed">
          Subsections that filter by status use an exact match on the <strong>name</strong> field in the statuses table — not by ID, not by color, but by string name.
        </p>
        <div className="space-y-3">
          <div className="flex gap-3 p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
            <div className="w-8 h-8 rounded-md bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
              <span className="wiki-mono text-xs font-bold text-primary-500">1</span>
            </div>
            <div>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-0.5">Look up IDs by name</p>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                The system searches the <Tag>talent_statuses</Tag> table for all records where <Tag>name</Tag> matches the given value (for the given talent_type). Gets a list of IDs.
              </p>
            </div>
          </div>
          <div className="flex gap-3 p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
            <div className="w-8 h-8 rounded-md bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
              <span className="wiki-mono text-xs font-bold text-primary-500">2</span>
            </div>
            <div>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-0.5">Filter talents</p>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                Talents are filtered by <Tag>current_status_id IN [found IDs]</Tag>. If multiple names are passed — OR logic is used (any of them).
              </p>
            </div>
          </div>
          <div className="flex gap-3 p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
            <div className="w-8 h-8 rounded-md bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-amber-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-0.5">Important: exact match</p>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                Case and spaces matter. <Tag color="green">Interview</Tag> and <Tag color="amber">interview</Tag> are different values. If your status is named differently than what the subsection expects — the candidate won't appear there. Check exact names in the <strong>Statuses</strong> section.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary table */}
      <div>
        <SubSectionHeading id="nav-summary">Summary table</SubSectionHeading>
        <Table
          columns={SUMMARY_COLUMNS}
          data={SUMMARY_DATA}
          rowKey={navRowKey}
        />
      </div>

      {/* FAQ */}
      <div>
        <SubSectionHeading id="nav-faq">Common Questions</SubSectionHeading>
        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
          {faqItems.map(({ key, question, answer }) => (
            <div key={key} id={key} className="scroll-mt-6 group">
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    onClick={() => toggleBlock(key)}
                    className="text-sm font-medium text-left text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 transition-colors"
                  >
                    {question}
                  </button>
                  <AnchorLink id={key} />
                </div>
                <button onClick={() => toggleBlock(key)} className="flex-shrink-0 ml-4">
                  <ChevronDown className={['w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary transition-transform duration-200', expandedBlocks[key] ? 'rotate-180' : ''].join(' ')} />
                </button>
              </div>
              {expandedBlocks[key] && <div className="pb-4 pr-6">{answer}</div>}
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}

export default NavigationSectionsGuide
